import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import FullScreenDialog from '@erikdakoda/ui/components/FullScreenDialog';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import type { AiMessageForUi } from '@erikdakoda/convo/types';
import { extractArtifactInputsFromMessages } from '@erikdakoda/artifact/shared/extractArtifactInputsFromMessages';
import { useArtifactStore } from '../hooks/useArtifactStore';
import EmbeddedArtifact from './EmbeddedArtifact';
import ArtifactCopyDownloadButtons from './ArtifactCopyDownloadButtons';
import { pushArtifactToPanel } from '../shared/pushArtifactToPanel';
import { syncArtifactStoreFromMessageInputs } from '../shared/syncArtifactStoreFromMessageInputs';
import { getArtifactType } from '../shared/artifactRegistry';

type ArtifactFullscreenDialogProps = {
  /** Convo messages used to refresh artifact content from the latest tool input. */
  messages?: readonly AiMessageForUi[];
};

/**
 * Fullscreen artifact dialog mounted at the chat root so it can open without the side panel.
 */
export default function ArtifactFullscreenDialog({
  messages = [],
}: ArtifactFullscreenDialogProps): React.ReactElement | null {
  'use memo';

  const { t } = useTranslation();
  const artifacts = useArtifactStore((state) => state.artifacts);
  const activeIdentifier = useArtifactStore((state) => state.activeIdentifier);
  const fullscreen = useArtifactStore((state) => state.fullscreen);
  const setFullscreen = useArtifactStore((state) => state.setFullscreen);
  const bumpContentRevision = useArtifactStore((state) => state.bumpContentRevision);
  const contentRevision = useArtifactStore((state) =>
    activeIdentifier ? (state.contentRevision[activeIdentifier] ?? 0) : 0,
  );

  const artifact = activeIdentifier ? artifacts[activeIdentifier] : null;

  useEffect(() => {
    if (fullscreen && !artifact) {
      setFullscreen(false);
    }
  }, [fullscreen, artifact, setFullscreen]);

  if (!fullscreen || !artifact) {
    return null;
  }

  const handleRefresh = (): void => {
    if (!activeIdentifier) {
      return;
    }

    const latestInput = extractArtifactInputsFromMessages(messages).find(
      (input) => input.identifier === activeIdentifier,
    );
    if (latestInput) {
      syncArtifactStoreFromMessageInputs([latestInput]);
    }

    bumpContentRevision(activeIdentifier);
  };

  return (
    <FullScreenDialog
      className="artifact-fullscreen-dialog"
      open={fullscreen}
      onClose={() => setFullscreen(false)}
      titleId="artifact-fullscreen-title"
      title={artifact.title}
      closeTitle={t('artifact-ui:artifactCloseFullscreen')}
      titleStart={
        <Tooltip title={getArtifactType(artifact.type)?.name ?? artifact.type} enterDelay={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <FaIconUvilo
              size="lg"
              icon={getArtifactType(artifact.type)?.icon ?? 'file'}
              prefix="fat"
            />
          </Box>
        </Tooltip>
      }
      actions={
        <>
          <PowerIconButton
            size="small"
            padding={2}
            sx={{ my: -2 }}
            icon="arrow-rotate-right"
            title={t('artifact-ui:artifactRefresh')}
            onClick={handleRefresh}
          />
          <ArtifactCopyDownloadButtons
            type={artifact.type}
            content={artifact.content}
            title={artifact.title}
          />
        </>
      }
    >
      <Box
        className="artifact-fullscreen-body"
        sx={{ height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}
      >
        <EmbeddedArtifact
          type={artifact.type}
          content={artifact.content}
          title={artifact.title}
          onOpenArtifact={pushArtifactToPanel}
          fillPanel
          contentReloadKey={contentRevision}
        />
      </Box>
    </FullScreenDialog>
  );
}
